import { useRef, useState } from "react";
import { NavigationType, useLocation, useNavigate, useNavigationType } from "react-router";

interface CreatedChatState {
  createdChat: true;
  /** The history entry of the new chat it was created from. */
  from: string;
}

/** Navigation state for a chat this page just created from the new chat at history entry `from`. */
export const createdChatState = (from: string): CreatedChatState => ({ createdChat: true, from });

/** The new chat's history entry this navigation created a chat from, if it did. */
function createdFrom(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const { createdChat, from } = state as Partial<CreatedChatState>;
  return createdChat === true && typeof from === "string" ? from : null;
}

/**
 * Changes when the person moves to another chat, so what they typed, attached or picked in one chat never
 * follows them into the next (MHS-1039).
 *
 * - An open chat is its uuid.
 * - A new chat is its history entry: "+ New chat", ⌘K's "Chat with <agent>", a space's or agent's link and Back
 *   each open another one, even on the same URL.
 * - A new chat getting its id isn't a move when this page created it from that very new chat
 *   (`useOpenCreatedChat`): attaching a file or sending the first message creates the chat and changes the URL,
 *   and that's still the same chat. Going Back or Forward to such an entry later is a move.
 */
export function useChatSwitchKey(sessionUuid: string | undefined): number {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entry = sessionUuid ? null : location.key;
  const from = navigationType === NavigationType.Push ? createdFrom(location.state) : null;
  const [seen, setSeen] = useState({ uuid: sessionUuid, entry, key: 0 });
  if (seen.uuid !== sessionUuid || seen.entry !== entry) {
    const sameChat = seen.uuid === undefined && sessionUuid !== undefined && from !== null && from === seen.entry;
    setSeen({ uuid: sessionUuid, entry, key: sameChat ? seen.key : seen.key + 1 });
  }
  return seen.key;
}

/**
 * Runs `reset` while rendering when the person has moved to another chat (`chatKey` from `useChatSwitchKey`), so
 * per-chat state the caller keeps (`reset` sets it back) never shows in the other chat, even for a frame.
 */
export function useOnChatSwitch(chatKey: number, reset: () => void) {
  const [shown, setShown] = useState(chatKey);
  if (shown !== chatKey) {
    setShown(chatKey);
    reset();
  }
}

/**
 * Opens the chat this page just created (attaching a file, or sending the first message) in place of the new chat it
 * was created from, as the same chat. Once the person has moved on (another chat, or another new chat), it leaves
 * them where they are: the chat is saved and in their history, and nothing typed since is carried into it.
 */
export function useOpenCreatedChat(): (path: string) => void {
  const location = useLocation();
  const navigate = useNavigate();
  const current = useRef(location.key);
  current.current = location.key;
  const from = location.key;
  return (path) => {
    if (current.current === from) navigate(path, { state: createdChatState(from) });
  };
}
